import { Reveal } from "./shared";

const Section = ({ title, children }) => (
    <div className="mt-10">
        <h2 className="text-xl font-bold text-white tracking-tight">{title}</h2>
        <div className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed space-y-3">{children}</div>
    </div>
);

const LegalLayout = ({ eyebrow, title, updated, children, testid }) => (
    <section className="pt-32 sm:pt-40 pb-24 sm:pb-32 relative" data-testid={testid}>
        <div className="absolute top-0 left-1/4 w-[500px] h-[400px] bg-cyan-500/[0.06] blur-[140px] rounded-full pointer-events-none" />
        <div className="container-x relative max-w-3xl">
            <Reveal>
                <p className="ff-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-4">{eyebrow}</p>
            </Reveal>
            <Reveal delay={0.08}>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.1]">{title}</h1>
            </Reveal>
            <Reveal delay={0.16}>
                <p className="mt-4 ff-mono text-xs uppercase tracking-[0.2em] text-slate-500">Last updated: {updated}</p>
            </Reveal>
            <Reveal delay={0.2}>
                <div className="mt-6 h-px w-full bg-gradient-to-r from-cyan-400/40 via-white/10 to-transparent" />
            </Reveal>
            {children}
        </div>
    </section>
);

export function PrivacyPolicy() {
    return (
        <LegalLayout eyebrow="Legal" title="Privacy Policy" updated="July 2026" testid="privacy-policy-page">
            <Section title="1. Overview">
                <p>
                    Flowtech ("we", "us", "our") helps businesses automate customer communication, lead follow-up, appointment
                    booking, and reviews. This Privacy Policy explains what information we collect through this website and our
                    services, and how we use it.
                </p>
            </Section>
            <Section title="2. Information We Collect">
                <p>When you submit a form on this website or use our services, we may collect:</p>
                <p>
                    Your name, business name, email address, and phone number; details about your business and what you would
                    like to automate; and any information you share with us during conversations, demos, or onboarding.
                </p>
                <p>
                    We also collect standard technical data such as browser type, device, and pages visited, used only to improve
                    the website experience.
                </p>
            </Section>
            <Section title="3. How We Use Your Information">
                <p>
                    We use your information to respond to your enquiry, provide demos and audits, deliver and improve our
                    automation services, and — only where you have agreed — to send you relevant updates about our services.
                </p>
            </Section>
            <Section title="4. SMS, Email & Communication Consent">
                <p>
                    By submitting your phone number or email through our forms, you consent to being contacted by Flowtech about
                    your enquiry by SMS, email, or phone. Message and data rates may apply. You can opt out of SMS at any time by
                    replying STOP, or unsubscribe from email using the link provided.
                </p>
            </Section>
            <Section title="5. How We Share Information">
                <p>
                    We do not sell your personal information. We share it only with the service providers needed to operate our
                    business (for example, our CRM, scheduling, and messaging platforms) and only as required to deliver the
                    service you requested.
                </p>
            </Section>
            <Section title="6. Data Security & Retention">
                <p>
                    We use reasonable administrative and technical safeguards to protect your information and keep it only as long
                    as needed to provide our services or meet legal obligations.
                </p>
            </Section>
            <Section title="7. Your Rights">
                <p>
                    You may request access to, correction of, or deletion of your personal information at any time by contacting
                    us through the details provided on this website.
                </p>
            </Section>
            <Section title="8. Cookies & Third-Party Links">
                <p>
                    This website may use cookies or similar technologies for analytics and performance. Our site may link to
                    third-party services (such as scheduling or messaging platforms) which are governed by their own privacy
                    policies.
                </p>
            </Section>
            <Section title="9. Changes to This Policy">
                <p>
                    We may update this policy from time to time. The latest version will always be posted on this page with its
                    revision date.
                </p>
            </Section>
            <Section title="10. Contact">
                <p>
                    Questions about this Privacy Policy? Email us at{" "}
                    <a href="mailto:admin@flowtechsolutions.site" className="text-cyan-300 hover:text-cyan-200 transition-colors duration-300">
                        admin@flowtechsolutions.site
                    </a>{" "}
                    or use the contact form on this website.
                </p>
            </Section>
        </LegalLayout>
    );
}

export function TermsAndConditions() {
    return (
        <LegalLayout eyebrow="Legal" title="Terms & Conditions" updated="July 2026" testid="terms-and-conditions-page">
            <Section title="1. Agreement">
                <p>
                    By using this website or engaging Flowtech's services, you agree to these Terms & Conditions. If you do not
                    agree, please do not use this website or our services.
                </p>
            </Section>
            <Section title="2. Our Services">
                <p>
                    Flowtech provides AI-powered business automation services, including lead response, missed-call text-back,
                    appointment booking, follow-up, review requests, and related customer communication workflows. Specific
                    deliverables, pricing, and timelines are agreed individually with each client before work begins.
                </p>
            </Section>
            <Section title="3. No Guaranteed Outcomes">
                <p>
                    Examples, demonstrations, calculators, and case studies on this website are illustrative. While we build
                    systems designed to capture more leads and save time, we do not guarantee specific revenue, conversion, or
                    booking results, as these depend on factors outside our control.
                </p>
            </Section>
            <Section title="4. Acceptable Use">
                <p>
                    You agree not to misuse this website or our services — including using them for unlawful, deceptive, or
                    spam-related messaging — and to comply with all applicable communication and privacy laws when contacting your
                    own customers.
                </p>
            </Section>
            <Section title="5. Communications">
                <p>
                    By submitting your contact details through our forms, you consent to receive communications from Flowtech by
                    SMS, email, or phone regarding your enquiry. You may opt out at any time by replying STOP to SMS or using the
                    unsubscribe link in emails.
                </p>
            </Section>
            <Section title="6. Intellectual Property">
                <p>
                    All content, design, and branding on this website belong to Flowtech and may not be copied or reused without
                    written permission. Custom automations built for clients are governed by the individual service agreement.
                </p>
            </Section>
            <Section title="7. Disclaimers & Limitation of Liability">
                <p>
                    This website and its content are provided "as is" without warranties of any kind. To the fullest extent
                    permitted by law, Flowtech is not liable for indirect, incidental, or consequential damages arising from use
                    of this website or our services.
                </p>
            </Section>
            <Section title="8. Third-Party Services">
                <p>
                    Our services may integrate with third-party platforms (such as CRM, calendar, and messaging tools). We are not
                    responsible for the availability, terms, or policies of those third-party services.
                </p>
            </Section>
            <Section title="9. Changes to These Terms">
                <p>
                    We may update these terms from time to time. Continued use of the website or services after changes are posted
                    constitutes acceptance of the updated terms.
                </p>
            </Section>
            <Section title="10. Governing Law">
                <p>
                    These Terms & Conditions are governed by and interpreted under the laws of the Republic of the Philippines,
                    and any disputes arising from them will be subject to the exclusive jurisdiction of the courts of the
                    Philippines.
                </p>
            </Section>
            <Section title="11. Contact">
                <p>
                    Questions about these Terms & Conditions? Email us at{" "}
                    <a href="mailto:admin@flowtechsolutions.site" className="text-cyan-300 hover:text-cyan-200 transition-colors duration-300">
                        admin@flowtechsolutions.site
                    </a>{" "}
                    or use the contact form on this website.
                </p>
            </Section>
        </LegalLayout>
    );
}
